import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { LogIn, Loader, AlertCircle, GraduationCap, Sparkles } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Phase 4 — Unified Winhouse Portal login.
 *
 * One login page that auto-routes by role after success:
 *   admin   -> /admin/dashboard
 *   tutor   -> /tutor/dashboard
 *   student -> /student/dashboard
 *   parent  -> /student/dashboard  (read-only future phase)
 *
 * The portal works alongside the existing tokenized classroom join links — a
 * student / tutor who already has a join link can still use it; this gives
 * them a persistent home base for past lessons + recaps + homework.
 */
const PortalLoginPage = ({ defaultRole }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, login, loading: authLoading } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const roleHome = (role) => {
    if (role === 'admin' || role === 'owner') return '/admin/dashboard';
    if (role === 'tutor') return '/tutor/dashboard';
    if (role === 'student' || role === 'parent') return '/student/dashboard';
    if (role === 'support' || role === 'finance' || role === 'staff') return '/staff/dashboard';
    return '/';
  };

  useEffect(() => {
    if (user && user.role) {
      const from = location.state?.from?.pathname || roleHome(user.role);
      navigate(from, { replace: true });
    }
  }, [user, navigate, location]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    const r = await login(email, password);
    if (!r.success) setError(r.error);
    setSubmitting(false);
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6]">
        <Loader className="w-7 h-7 text-red-700 animate-spin" />
      </div>
    );
  }

  const headline = defaultRole === 'tutor' ? 'Tutor Portal' : defaultRole === 'student' ? 'Student Portal' : 'Winhouse Portal';
  const subline = defaultRole === 'tutor'
    ? 'Sign in to view your assigned classes, lesson notes and earnings.'
    : defaultRole === 'student'
    ? 'Sign in to view your upcoming lessons, recaps and homework.'
    : 'Sign in to your Winhouse account.';

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-[#FAF9F6]" data-testid="portal-login-page">
      {/* Brand panel */}
      <div className="lg:w-1/2 bg-black text-white p-10 lg:p-16 flex flex-col justify-between relative overflow-hidden">
        <div>
          <div className="text-[#D3D86F] text-xs uppercase tracking-[3px] font-bold mb-2">WINHOUSE TUTORS</div>
          <div className="text-3xl lg:text-4xl font-bold leading-tight">{headline}</div>
          <div className="text-sm text-gray-300 mt-3 max-w-md">{subline}</div>
        </div>
        <div className="mt-10 lg:mt-0">
          <div className="flex items-center gap-2 text-[#D3D86F]"><Sparkles className="h-3 w-3" /><span className="text-[10px] uppercase tracking-[2px]">AI Lesson Recaps · Branded PDFs · Live Classroom</span></div>
          <div className="text-[11px] text-gray-400 mt-2">winhousetutors.academy</div>
        </div>
        <div className="absolute -bottom-32 -right-32 w-64 h-64 rounded-full bg-[#D3D86F]/10 blur-3xl" />
      </div>

      {/* Form panel */}
      <div className="lg:w-1/2 flex items-center justify-center p-8 lg:p-16">
        <form onSubmit={handleSubmit} className="w-full max-w-md space-y-5" data-testid="portal-login-form">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[2px] text-gray-500 font-bold">
            <GraduationCap className="h-4 w-4 text-red-700" /> Sign in
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1 block">Email</label>
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="w-full h-11 px-4 rounded-xl border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-red-200 text-sm"
              data-testid="portal-email-input" autoComplete="email" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1 block">Password</label>
            <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="w-full h-11 px-4 rounded-xl border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-red-200 text-sm"
              data-testid="portal-password-input" autoComplete="current-password" />
          </div>
          {error && (
            <div className="flex items-start gap-2 p-3 rounded-xl bg-red-50 border border-red-200" data-testid="portal-login-error">
              <AlertCircle className="h-4 w-4 text-red-700 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-red-900">{error}</div>
            </div>
          )}
          <button type="submit" disabled={submitting}
            className="w-full h-11 rounded-full bg-red-700 hover:bg-red-800 text-white text-sm font-bold uppercase tracking-wider flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="portal-login-submit">
            {submitting ? <Loader className="h-4 w-4 animate-spin" /> : <LogIn className="h-4 w-4" />}
            {submitting ? 'Signing in…' : 'Sign in'}
          </button>
          <div className="text-center">
            <Link to="/forgot-password" className="text-[12px] text-gray-600 hover:text-red-700 hover:underline" data-testid="portal-forgot-password-link">
              Forgot your password?
            </Link>
          </div>
          <div className="text-[11px] text-gray-500 text-center pt-2">
            {defaultRole === 'student' ? (
              <>New student? <Link to="/student/signup" className="underline text-red-700 font-bold" data-testid="signup-link-student">Create your free account</Link></>
            ) : defaultRole === 'tutor' ? (
              <>Tutor? <Link to="/tutor/signup" className="underline text-red-700 font-bold" data-testid="signup-link-tutor">Apply for portal access</Link></>
            ) : defaultRole === 'staff' ? (
              <>Staff member? <Link to="/staff/signup" className="underline text-red-700 font-bold" data-testid="signup-link-staff">Request staff access</Link></>
            ) : (
              <>New here? <Link to="/student/signup" className="underline text-gray-700" data-testid="signup-link-student">Create student account</Link> · <Link to="/tutor/signup" className="underline text-gray-700" data-testid="signup-link-tutor">Apply as tutor</Link> · <Link to="/staff/signup" className="underline text-gray-700" data-testid="signup-link-staff">Staff request</Link></>
            )}
          </div>
          <div className="text-[11px] text-gray-400 text-center">
            Or email <a href="mailto:info@winhousetutors.com" className="underline">info@winhousetutors.com</a>
          </div>
          <div className="text-[11px] text-gray-400 text-center pt-1">
            <Link to="/" className="hover:text-gray-700">← Back to Winhouse</Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PortalLoginPage;
